/**
 * Real HTTP implementation of RefereeApiClient — built on the generated `@bymyword/api-types`.
 *
 * Transport is the generated `refereeClient` (re-exported through `./api-types`): it attaches the
 * emailed `selector.verifier` token as `X-Referee-Token` and sends `credentials: 'include'` so the
 * elevated post-accept session cookie (`bmw_ref_session`, HttpOnly) rides along. It is NOT a JWT
 * bearer client. Base URL is `VITE_API_BASE_URL` (blank → same-origin). A non-2xx response surfaces
 * as the generated `ApiError`, folded into the app's uniform, HTTP-free `RefereeApiError` taxonomy.
 *
 * WP-F4 wire→view-model mapping (the generated document now TYPES the referee/audio 200 responses,
 * so the transport consumes the generated component schemas directly — no hand-written casts):
 *  - `GET /context` returns the generated `RefereeContextResponse`; `mapRefereeContext` folds it
 *    into the app view-model (see `referee-wire.ts`). Every backend denial (unknown/expired/revoked/
 *    terminal token) collapses to an opaque `404 invalid_link`, mapped to `RefereeApiError('not_found')`.
 *  - The ACTION endpoints (accept/decline/confirm/consent) return only `{ status }`, NOT a context.
 *    The interface promises a `RefereeContext`, so:
 *      · accept  — POSTs (server Set-Cookies the session), then re-reads context and maps it.
 *      · confirm — POSTs, then re-reads context (status stays `accepted`; confirm/consent are
 *        client sub-steps that do not advance the server status — see `steps.furthestStepFor`).
 *      · decline — the POST REVOKES the token, so a follow-up context read would 404. It therefore
 *        reads context FIRST (token still live), POSTs, then returns that context with `declined`.
 *  - Audio init/complete (WP-B12) map exact byte length / storage key.
 *  - The draft endpoints (WP-B14) are live: poll/edit/regenerate. NOTE their credential differs —
 *    they authenticate with the ELEVATED SESSION COOKIE, not the emailed `X-Referee-Token`, and
 *    read the bound request id from the session's `sub` claim, so no id is ever sent in a body.
 *    This client attaches both on every call; the one place the distinction shows is a 401 from
 *    a cookie that lapsed under a still-valid link, which `withSessionRecovery` heals in place.
 *  - The sign endpoints (WP-B15a) are live: `GET /api/referee/sign` fetches the disclosures the
 *    ceremony must display (attestation wording + version + the name the signature is checked
 *    against), and `POST` commits the signature. Same session-cookie credential as the draft verbs.
 *    Signing is IDEMPOTENT — a repeat returns the original verification code with 200, so there is
 *    no already-signed error path to map here.
 *  The mock seam (`api-client.mock.ts`) drives every screen in dev/demo regardless.
 */
import type {
  AudioUploadTicket,
  ConsentAttestation,
  CorrectNameResult,
  RefereeContext,
  SignDisclosures,
  SignResult,
} from './api-types';
import { ApiError, RefereeApiError, refereeClient } from './api-types';
import { mapDraftResponse, mapDraftWrite, mapRefereeContext } from './referee-wire';
import { AudioUploadError, putAudioBlob } from './recorder';
import type { RefereeApiClient, RefereeToken } from './api-client';

// Same-origin (blank VITE_API_BASE_URL) must still resolve to an ABSOLUTE URL for the generated
// client's URL() construction, so fall back to the page origin.
const BASE =
  (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '') ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// The legal document a referee attests to on consent — used ONLY as the graceful fallback when the
// resolved context did not carry the current attestation (`consentDocumentKey`/`Version`). The happy
// path posts the exact key + version `/context` surfaced, because the backend rejects a mismatched
// version with 422 `consent_version_mismatch` (`legal_documents.doc_type` = `referee_attestation`).
const REFEREE_CONSENT_DOCUMENT = 'referee_attestation';
const REFEREE_CONSENT_VERSION = '1';

function errorKind(status: number): RefereeApiError['kind'] {
  if (status === 401) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 410) return 'gone';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  return 'server';
}

/** A referee-token client bound to this call's token (per-request, matching the interface shape). */
function refereeApi(token: RefereeToken) {
  return refereeClient(BASE, token);
}

/**
 * Run a POST-ACCEPT verb (draft/sign/audio — the ones authenticated by the elevated session
 * COOKIE, not the emailed token) with one transparent session recovery.
 *
 * The cookie lives ~2 hours; the emailed link lives ~14 days, and the flow is explicitly designed
 * to be resumed from that email across days. So a 401 here almost always means "the cookie lapsed
 * under a still-valid link" — the ordinary next-morning resume, not an attack. The backend's
 * designed recovery is `POST /accept`: re-entrant for every post-accept status, it re-mints the
 * session with no state transition and no duplicate candidate notification (WP-B11, "re-entrant
 * until terminal"). Recover once and retry the original call — the referee never sees the seam.
 * Retrying is safe on every wrapped verb: a 401 is answered before any side effect, and sign is
 * idempotent by design (a repeat is a 200 carrying the original code).
 *
 * If the re-mint is refused (the request left the resumable states — signed in another tab,
 * withdrawn, expired) or the retry still 401s (a browser that will not hold the cookie), the
 * ORIGINAL 401 surfaces — mapped to kind `unauthorized` — so screens re-read the context and let
 * the step guard land the referee somewhere true, exactly as they do for a 409.
 */
async function withSessionRecovery<T>(token: RefereeToken, call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    try {
      await refereeApi(token).post('/api/referee/accept');
    } catch {
      throw error; // surface the original 401, never the refused re-mint's own status
    }
    return await call();
  }
}

/** Translate a transport failure to the app's uniform, HTTP-free error taxonomy. */
function toRefereeApiError(error: unknown): RefereeApiError {
  if (error instanceof RefereeApiError) return error;
  if (error instanceof ApiError) {
    // The referee/audio error bodies are `{ error: <machine code> }` (e.g. `consent_required`,
    // `consent_version_mismatch` off a 422). Carry that code so callers can branch on the specific
    // denial without re-inspecting HTTP; the message stays generic (no URL/token leakage).
    const code = typeof error.problem?.error === 'string' ? error.problem.error : undefined;
    return new RefereeApiError(`Request failed (${error.status})`, errorKind(error.status), error.status, code);
  }
  // Network/DNS/CORS failure — never surface the raw error (it can carry the URL/token).
  return new RefereeApiError('Network request failed', 'network');
}

async function fetchContext(token: RefereeToken): Promise<RefereeContext> {
  // The generated client types this 200 as `RefereeContextResponse` — no cast needed.
  const wire = await refereeApi(token).get('/api/referee/context');
  return mapRefereeContext(wire);
}

export function createHttpClient(): RefereeApiClient {
  return {
    async getContext(token) {
      try {
        return await fetchContext(token);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async accept(token) {
      try {
        // POST sets the HttpOnly elevated-session cookie (credentials:'include' on the client);
        // re-read context so the caller gets the real post-accept state to seed the guards.
        await refereeApi(token).post('/api/referee/accept');
        return await fetchContext(token);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async decline(token, req) {
      try {
        // The token is revoked BY the decline, so read the context (candidate name, etc.) while it
        // still resolves, then post. A read AFTER decline would 404 — return the pre-decline
        // context marked `declined` so the calm terminal page still greets the referee by name.
        const before = await fetchContext(token);
        await refereeApi(token).post('/api/referee/decline', {
          body: { reason: req?.reason ?? null },
        });
        return { ...before, status: 'declined' };
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async confirmDetails(token, req) {
      try {
        await refereeApi(token).post('/api/referee/confirm-details', {
          body: {
            fullName: req.name,
            relationshipContext: req.relationship ?? null,
            companyName: req.company ?? null,
          },
        });
        // Confirm-details does not advance the server status (it stays `accepted`); re-read so the
        // caller seeds the freshest context, and the record step unlocks via `furthestStepFor`.
        return await fetchContext(token);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async recordConsent(token, consent?: ConsentAttestation) {
      try {
        // Post the EXACT attestation `/context` surfaced (the caller passes it from the resolved
        // context). Fall back to the default doc/version only when the context didn't carry one —
        // a stale version is rejected 422 `consent_version_mismatch`.
        await refereeApi(token).post('/api/referee/consent', {
          body: {
            documentKey: consent?.documentKey ?? REFEREE_CONSENT_DOCUMENT,
            version: consent?.version ?? REFEREE_CONSENT_VERSION,
          },
        });
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async createRealtimeSession(token, offerSdp) {
      try {
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/realtime/session', {
            body: { offerSdp },
          }),
        );
        return {
          answerSdp: wire.answerSdp,
          sessionId: wire.sessionId,
          sessionProof: wire.sessionProof,
          expiresAt: wire.expiresAt,
          model: wire.model,
        };
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async extractLiveKeyPoints(token, req) {
      try {
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/realtime/key-points', {
            body: {
              sessionId: req.sessionId,
              sessionProof: req.sessionProof,
              transcript: req.content,
            },
          }),
        );
        return wire.keyPoints;
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async initAudioUpload(token, req) {
      try {
        // `contentLength` is the exact recorded byte length the backend signs the PUT against.
        // The generated client types this 200 as `AudioInitResponse` — no cast needed.
        // Session-cookie verb: a lapsed session is re-minted + retried once (withSessionRecovery).
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/audio/init', {
            body: { sizeBytes: req.contentLength, contentType: req.contentType },
          }),
        );
        // WP-F5 wire→view-model: fold the presign into the app's upload ticket. The storage key is
        // the id we hand back at complete; the PUT is always `PUT` (WP-B12) with the signed headers.
        return {
          uploadId: wire.storageKey,
          url: wire.uploadUrl,
          method: 'PUT',
          headers: wire.headers ?? {},
        } satisfies AudioUploadTicket;
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async uploadAudio(ticket, blob) {
      try {
        // The single retryable constrained PUT to the first-party R2 host (see _headers connect-src).
        await putAudioBlob(ticket, blob);
      } catch (error) {
        // Fold into the app taxonomy so the record step can offer retry (network) vs re-record.
        if (error instanceof AudioUploadError) {
          throw new RefereeApiError('Recording upload failed', error.kind === 'network' ? 'network' : 'server');
        }
        throw toRefereeApiError(error);
      }
    },

    async completeAudioUpload(token, req) {
      try {
        // The SPA's ticket `uploadId` IS the storage key returned by init; `sizeBytes` is the exact
        // PUT length the server HEAD-verifies (WP-B12 rejects a mismatch), duration is advisory.
        await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/audio/complete', {
            body: {
              storageKey: req.uploadId,
              sizeBytes: req.sizeBytes,
              durationSeconds: req.durationSeconds ?? null,
              realtimeTranscript: req.realtimeTranscript
                ? {
                    sessionId: req.realtimeTranscript.sessionId,
                    sessionProof: req.realtimeTranscript.sessionProof,
                    content: req.realtimeTranscript.content,
                  }
                : null,
            },
          }),
        );
        return await fetchContext(token);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async pollDraft(token) {
      try {
        // Credential here is the elevated SESSION COOKIE, not the emailed token (WP-B14): the
        // bound request id comes off the session's `sub` claim, which is why no id is sent — and
        // why there is no id to get wrong. The generated client types this 200 as `DraftResponse`.
        // A lapsed cookie (the next-morning resume) is re-minted + retried once, invisibly.
        const wire = await withSessionRecovery(token, () => refereeApi(token).get('/api/referee/draft'));
        return mapDraftResponse(wire);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async editDraft(token, req) {
      try {
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/draft/edit', {
            body: { body: req.letterBody },
          }),
        );
        return mapDraftWrite(wire);
      } catch (error) {
        throw toRefereeApiError(error);
      }
    },

    async regenerateDraft(token, req) {
      try {
        // `tone` is the backend's LetterTone vocabulary verbatim — an unknown value is a 400.
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/draft/regenerate', {
            body: { tone: req.tone },
          }),
        );
        return mapDraftWrite(wire);
      } catch (error) {
        // The cap (409 `regeneration_cap_reached`) folds into the uniform taxonomy as a `conflict`
        // carrying its code, so the review screen can tell a spent button (editing still live)
        // from a letter that can no longer change at all. Deliberately NOT a 429 upstream: waiting
        // never helps, so nothing here should invite a retry.
        throw toRefereeApiError(error);
      }
    },

    async getSignDisclosures(token) {
      try {
        // Same credential note as the draft endpoints: the elevated session cookie carries this,
        // and the bound request id comes off its `sub` claim — no id is ever sent. The generated
        // client types this 200 as `SignDisclosuresResponse`, so the four fields below are the
        // contract, not an assumption.
        const wire = await withSessionRecovery(token, () => refereeApi(token).get('/api/referee/sign'));
        return {
          signerName: wire.signerName,
          attestationText: wire.attestationText,
          documentKey: wire.documentKey,
          documentVersion: wire.documentVersion,
        } satisfies SignDisclosures;
      } catch (error) {
        // 409 `cannot_sign` folds into the uniform taxonomy as a `conflict` carrying its code. The
        // ceremony has no screen for an unsignable request, so the caller re-reads context and lets
        // the step guard move on rather than rendering a sign form that would only be refused.
        throw toRefereeApiError(error);
      }
    },

    async sign(token, req) {
      try {
        // The attestation pair is echoed back from the disclosures this screen actually displayed
        // — never a constant. The server re-resolves the current document and refuses a stale pair
        // (422 `attestation_version_mismatch`), which is precisely what stops a tab left open
        // across a legal-document republish from signing against wording nobody saw.
        // Session recovery is SAFE here of all places: sign is idempotent by design (a repeat is
        // a 200 carrying the original code), and the 401 is answered before any side effect.
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/sign', {
            body: {
              typedName: req.typedName,
              documentKey: req.documentKey,
              documentVersion: req.documentVersion,
            },
          }),
        );
        // `verificationCode` is passed through UNTOUCHED — it is the canonical 24-char form a
        // verify lookup is keyed on. Grouping it for readability belongs to the PDF and the
        // signed-copy email, over the same value; doing it here would show a string that isn't it.
        return { verificationCode: wire.verificationCode, signedAt: wire.signedAt } satisfies SignResult;
      } catch (error) {
        // Every sign failure lands here as a coded RefereeApiError:
        //   422 name_mismatch / attestation_version_mismatch / unknown_document
        //   409 cannot_sign · 400 invalid_signature · 404 not_found
        // Note `errorKind` has no 422 case, so those surface as kind 'server' — callers branch on
        // `.code`, which is the precise signal and the reason the transport carries it at all.
        // An ALREADY-SIGNED request is not in this list: it is a 200 with the original code.
        throw toRefereeApiError(error);
      }
    },

    async correctName(token, req) {
      try {
        // No request id in the body — the same rule as sign. The elevated session cookie carries
        // the binding and the server reads the id off its `sub` claim; this verb writes the name a
        // legal artifact is ATTRIBUTED to, so a body-supplied id would be the same cross-tenant
        // hole reached one step earlier. A guard test pins the DTO's shape.
        const wire = await withSessionRecovery(token, () =>
          refereeApi(token).post('/api/referee/sign/correct-name', {
            body: { fullName: req.fullName },
          }),
        );
        return {
          signerName: wire.signerName,
          previousNameInLetter: wire.previousNameInLetter,
        } satisfies CorrectNameResult;
      } catch (error) {
        // 400 invalid_name · 409 cannot_correct_name (includes already-signed) · 404 not_found.
        throw toRefereeApiError(error);
      }
    },
  };
}
